// Préparation des documents dans le navigateur : chaque page devient une image JPEG
// (nette sur tablette, légère à stocker). Les PDF sont lus avec pdf.js, chargé seulement ici.
import type { DocKind, DocPage } from "../data/types.ts";

export const MAX_PAGES = 12;
const PAGE_PX = 1800;   // plus grand côté d'une page
const THUMB_PX = 360;   // vignette
const QUALITY = 0.82;

export class DocError extends Error {}

/** Devine le type de document d'après son nom. */
export function guessKind(name: string, mime: string): DocKind {
  const n = name.toLowerCase();
  if (/\bbat\b|bon.?a.?tirer|epreuve|épreuve|proof/.test(n)) return "bat";
  if (/maquette|mockup|gabarit|blanc/.test(n)) return "maquette";
  if (mime.startsWith("image/")) return "photo";
  return mime === "application/pdf" ? "bat" : "autre";
}

function canvasOf(w: number, h: number): HTMLCanvasElement {
  const c = document.createElement("canvas");
  c.width = Math.max(1, Math.round(w)); c.height = Math.max(1, Math.round(h));
  return c;
}

function toJpeg(src: CanvasImageSource, w: number, h: number, maxPx: number, quality = QUALITY): DocPage {
  const k = Math.min(1, maxPx / Math.max(w, h));
  const c = canvasOf(w * k, h * k);
  const ctx = c.getContext("2d")!;
  ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, c.width, c.height); // fond blanc pour les PNG transparents
  ctx.drawImage(src, 0, 0, c.width, c.height);
  return { data: c.toDataURL("image/jpeg", quality), width: c.width, height: c.height };
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new DocError("Image illisible : enregistrez-la en JPEG ou PNG puis réessayez."));
    img.src = url;
  });
}

/** Réduit une image déjà en data URL (vignette). */
export async function thumbOf(page: DocPage): Promise<string> {
  const img = await loadImage(page.data);
  return toJpeg(img, img.naturalWidth || page.width, img.naturalHeight || page.height, THUMB_PX, 0.7).data;
}

type PdfJs = {
  GlobalWorkerOptions: { workerSrc: string };
  getDocument: (src: { data: ArrayBuffer }) => { promise: Promise<PdfDoc>; destroy?: () => Promise<void> };
};
type PdfDoc = { numPages: number; getPage: (n: number) => Promise<PdfPage>; destroy?: () => Promise<void> };
type PdfPage = {
  getViewport: (o: { scale: number }) => { width: number; height: number };
  render: (o: { canvasContext: CanvasRenderingContext2D; canvas: HTMLCanvasElement; viewport: unknown }) => { promise: Promise<void> };
};

let pdfjs: Promise<PdfJs> | null = null;
function loadPdfJs(): Promise<PdfJs> {
  if (!pdfjs) {
    const base = new URL("vendor/pdfjs/", document.baseURI).href;
    pdfjs = (import(/* chargé à la demande */ base + "pdf.min.mjs") as Promise<PdfJs>).then((m) => {
      m.GlobalWorkerOptions.workerSrc = base + "pdf.worker.min.mjs";
      return m;
    }).catch((e) => { pdfjs = null; throw e; });
  }
  return pdfjs;
}

/** Transforme un fichier (PDF ou image) en pages prêtes à afficher. */
export async function fileToPages(file: File, onProgress?: (done: number, total: number) => void): Promise<{ pages: DocPage[]; thumb: string; truncated: boolean }> {
  const isPdf = file.type === "application/pdf" || /\.pdf$/i.test(file.name);
  if (file.size > 60 * 1024 * 1024) throw new DocError("Fichier trop lourd (60 Mo au maximum).");
  if (isPdf) {
    let lib: PdfJs;
    try { lib = await loadPdfJs(); } catch { throw new DocError("Le lecteur de PDF n'a pas pu se charger. Vérifiez la connexion puis réessayez."); }
    let doc: PdfDoc;
    const task = lib.getDocument({ data: await file.arrayBuffer() });
    try { doc = await task.promise; }
    catch { throw new DocError("Ce PDF n'a pas pu être lu (protégé par mot de passe ou abîmé ?)."); }
    const total = Math.min(doc.numPages, MAX_PAGES);
    const pages: DocPage[] = [];
    for (let n = 1; n <= total; n++) {
      onProgress?.(n - 1, total);
      const page = await doc.getPage(n);
      const v1 = page.getViewport({ scale: 1 });
      const scale = PAGE_PX / Math.max(v1.width, v1.height);
      const viewport = page.getViewport({ scale });
      const c = canvasOf(viewport.width, viewport.height);
      const ctx = c.getContext("2d")!;
      ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, c.width, c.height);
      await page.render({ canvasContext: ctx, canvas: c, viewport }).promise;
      pages.push({ data: c.toDataURL("image/jpeg", QUALITY), width: c.width, height: c.height });
    }
    const truncated = doc.numPages > MAX_PAGES;
    await (task.destroy ? task.destroy() : doc.destroy?.())?.catch?.(() => {});
    onProgress?.(total, total);
    return { pages, thumb: await thumbOf(pages[0]), truncated };
  }
  if (!file.type.startsWith("image/")) throw new DocError("Formats acceptés : PDF, JPEG, PNG, WebP.");
  const url = URL.createObjectURL(file);
  try {
    const img = await loadImage(url);
    const page = toJpeg(img, img.naturalWidth, img.naturalHeight, PAGE_PX);
    return { pages: [page], thumb: await thumbOf(page), truncated: false };
  } finally {
    URL.revokeObjectURL(url);
  }
}
